import { infraTypesByIdForIcons } from '../statics/constants/infraTypes';
const cdnBase = import.meta.env.VITE_AZURE_CDN;

const PROGRESS_COLOR_NORMAL = '#078CB3';
const PROGRESS_COLOR_OVERLOAD = '#F46A6A';
const PROGRESS_RING_WIDTH = 18;
const BACKGROUND_RING_COLOR = '#E8F4F8';


const iconCache = new Map();

export function generateProgressIconSvg(iconName, usage = 0, isSelected = false, expired = false) {
  const cacheKey = `${iconName}_${usage}_${isSelected}_${expired}`;

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }

  const size = 200;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size / 2) - (PROGRESS_RING_WIDTH / 2) - 4;
  const innerRadius = radius - PROGRESS_RING_WIDTH - 8;

  const progressPercentage = Math.min(Math.max(usage, 0), 100) / 100;
  const progressColor = usage > 100 ? PROGRESS_COLOR_OVERLOAD : PROGRESS_COLOR_NORMAL;


  const startAngle = -90;
  const endAngle = startAngle + (progressPercentage * 360);


  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad)
    };
  };

  const start = polarToCartesian(centerX, centerY, radius, startAngle);
  const end = polarToCartesian(centerX, centerY, radius, endAngle);
  const largeArcFlag = progressPercentage > 0.5 ? 1 : 0;


  const progressArc = progressPercentage > 0 && !expired && !isSelected
    ? `<path d="M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}" 
         stroke="${progressColor}" 
         stroke-width="${PROGRESS_RING_WIDTH}" 
         fill="none" 
         stroke-linecap="round"/>`
    : '';


  let bgColor = '#E3F5FB';
  if (expired) {
    bgColor = '#F5F5F5';
  } else if (isSelected) {
    bgColor = '#E8F8E8';
  }

  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <!-- Fondo del anillo -->
      <circle cx="${centerX}" cy="${centerY}" r="${radius}" 
              stroke="${BACKGROUND_RING_COLOR}" 
              stroke-width="${PROGRESS_RING_WIDTH}" 
              fill="none"/>
      
      <!-- Arco de progreso -->
      ${progressArc}
      
      <!-- Círculo interno con fondo -->
      <circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" fill="${bgColor}"/>
      
      <!-- Borde del círculo interno -->
      <circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" 
              stroke="#1C2A4E" 
              stroke-width="3" 
              fill="none"/>
      
      <!-- Pin/marcador inferior -->
      <path d="M ${centerX} ${size - 10} L ${centerX - 8} ${size - 30} L ${centerX + 8} ${size - 30} Z" 
            fill="${progressColor}" />
    </svg>
  `;

  const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
  iconCache.set(cacheKey, dataUrl);

  return dataUrl;
}


export async function generateDynamicUsageIcon(infra, selectedSensors) {
  const { infrastructureTypeId } = infra || {};
  const { usage = 0, expired } = infra.mainMeasurementTarget || {};


  const isSelected = selectedSensors.some(
    (sensor) => sensor.infrastructureId === infra.measurementeInfraestructureId,
  );


  const baseIconName = infraTypesByIdForIcons[infrastructureTypeId];
  let baseIconUrl;

  if (isSelected) {
    baseIconUrl = `${cdnBase}img/InfraIcons/png/${baseIconName}2.png`;
  } else if (expired) {
    baseIconUrl = `${cdnBase}img/InfraIcons/png/${baseIconName}_non_operational.png`;
  } else {
    baseIconUrl = `${cdnBase}img/InfraIcons/png/${baseIconName}.png`;
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const size = 200;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = baseIconUrl;

    img.onload = () => {
      const centerX = size / 2;
      const centerY = size / 2;


      ctx.clearRect(0, 0, size, size);


      ctx.drawImage(img, 0, 0, size, size);



      const ringRadius = 90;
      const ringWidth = PROGRESS_RING_WIDTH;


      if (usage > 0 && !expired && !isSelected) {
        const progressPercentage = Math.min(usage, 100) / 100;
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle - (progressPercentage * Math.PI * 2);


        const progressColor = usage > 100 ? PROGRESS_COLOR_OVERLOAD : PROGRESS_COLOR_NORMAL;

        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, startAngle, endAngle, true);
        ctx.strokeStyle = progressColor;
        ctx.lineWidth = ringWidth;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.closePath();
      }

      const base64Result = canvas.toDataURL('image/png');
      resolve(base64Result);
    };

    img.onerror = (err) => {
      console.error('Error al cargar la imagen base:', err);

      resolve(getImageUsageValue(infra, selectedSensors));
    };
  });
}

export function getImageUsageValue(infra, selectedSensors) {
  const { infrastructureTypeId } = infra || {};
  const { usage, expired } = infra.mainMeasurementTarget || {};


  const isSelected = selectedSensors.some(
    (sensor) => sensor.infrastructureId === infra.measurementeInfraestructureId,
  );

  if (isSelected) {
    return `${cdnBase}img/InfraIcons/png/${infraTypesByIdForIcons[infrastructureTypeId]}2.png`;
  }

  if (expired) {
    return `${cdnBase}img/InfraIcons/png/${infraTypesByIdForIcons[infrastructureTypeId]}_non_operational.png`;
  }


  const usageRanges = [
    { max: 0, suffix: '' },
    { max: 10, suffix: '_0-10' },
    { max: 20, suffix: '_10-20' },
    { max: 30, suffix: '_20-30' },
    { max: 40, suffix: '_30-40' },
    { max: 50, suffix: '_40-50' },
    { max: 60, suffix: '_50-60' },
    { max: 70, suffix: '_60-70' },
    { max: 80, suffix: '_70-80' },
    { max: 90, suffix: '_80-90' },
    { max: 100, suffix: '_90-100' },
    { max: Infinity, suffix: '_100' },
  ];

  const suffix = usageRanges.find((range) => usage <= range.max)?.suffix || '';

  const result = `${cdnBase}img/InfraIcons/png/${infraTypesByIdForIcons[infrastructureTypeId]}${suffix}.png`;

  return result;
}

export function generateInfraImageToInteractiveSimulation(
  infra,
  counter,
  isEdited,
) {
  const canvas = document.createElement('canvas');
  const size = 64;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');


  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = getImageUsageValue(infra, []);

  return new Promise((resolve, reject) => {
    img.onload = () => {

      ctx.drawImage(img, 0, 0, size, size);


      if (counter > 0) {
        const circleRadius = 16;
        const circleX = size - circleRadius;
        const circleY = circleRadius;


        ctx.beginPath();
        ctx.arc(circleX, circleY, circleRadius, 0, Math.PI * 2);
        ctx.fillStyle = isEdited ? '#17de17' : '#FFCC00';
        ctx.fill();
        ctx.closePath();


        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(counter.toString(), circleX, circleY);
        const base64Result = canvas.toDataURL();
        resolve(base64Result);
      } else if (infra.editableAttributes) {

        const editableIcon = new Image();
        editableIcon.crossOrigin = "anonymous";
        editableIcon.src = `${cdnBase}img/edit.png`;

        editableIcon.onload = () => {
          const iconSize = 32;
          const iconX = size - iconSize;
          const iconY = 1;
          ctx.drawImage(editableIcon, iconX, iconY, iconSize, iconSize);


          const base64Result = canvas.toDataURL();
          resolve(base64Result);
        };

        editableIcon.onerror = (err) => {
          console.error('Error al cargar la imagen editable:', err);
          reject(err);
        };
      } else {

        const base64Result = canvas.toDataURL();
        resolve(base64Result);
      }
    };

    img.onerror = (err) => {
      reject(err);
    };
  });
}

